import { Building2, Clock, Phone, X } from 'lucide-react';
import IconButton from 'components/crm/ui/IconButton';
import Tag from 'components/crm/ui/Tag';
import useText from 'hooks/useText';
import { attemptTag, sourceKey } from 'pages/prospection/callListItems';

// Tag tone of a call type: a follow-up is orange, a discovery primary.
export const TYPE_TONES = { discovery: 'primary', followup: 'orange' };

// « {source} · {city} », with « - » for what is missing.
export const sublineOf = (text, company) => {
  const key = company && sourceKey(company.source);
  return text('row.subline', {
    source: key ? text(key) : '-',
    city: (company && company.city) || '-',
  });
};

// The company block shared by a row to call and a call made; `faded` greys the
// name and the subline.
export const CompanyCell = ({ company, text, faded }) => (
  <>
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-line bg-white text-ink">
      <Building2 size={18} />
    </span>
    <span
      className={`flex min-w-0 flex-1 flex-col gap-0.5 font-title ${faded ? 'opacity-50' : ''}`}
    >
      <span className="truncate text-sm font-medium leading-5 text-ink">
        {(company && company.name) || '-'}
      </span>
      <span className="truncate text-xs leading-4 text-body">
        {sublineOf(text, company)}
      </span>
    </span>
  </>
);

/**
 * One call left to make, 64 px high: its rank (`rank`) or a clock for a
 * planned one, ringed in orange when `selected`; the company, the attempt tag,
 * then the phone button, or the remove cross while `editing`. The row itself
 * takes no click.
 */
const CallRow = ({
  line,
  rank,
  now,
  editing,
  selected,
  removeDisabled,
  onCall,
  onRemove,
}) => {
  const { text } = useText('prospection');
  const company = line.company || {};
  const tag = attemptTag(line, now);
  const label = text(tag.label.key, tag.label.values);
  const tagText = tag.suffix
    ? text(tag.suffix.key, { label, ...tag.suffix.values })
    : label;
  const callLabel = text('row.call', { phone: company.phone || '-' });
  const removeLabel = text(
    removeDisabled ? 'row.removeDisabled' : 'row.remove',
  );

  return (
    <div
      data-testid="call-row"
      data-selected={selected ? 'true' : 'false'}
      className={`flex h-16 items-center gap-3 border-b border-line px-4 last:border-b-0 ${selected ? 'bg-[#fcfcfc]' : 'bg-white'}`}
    >
      <span
        data-testid="call-row-rank"
        className={`flex size-7 shrink-0 items-center justify-center rounded-full border bg-white font-title text-xs font-medium ${selected ? 'border-primary text-primary-dark' : 'border-line text-ink'}`}
      >
        {rank ? rank : <Clock size={14} />}
      </span>
      <CompanyCell company={company} text={text} />
      <Tag tone={TYPE_TONES[tag.tone] || 'primary'} title>
        {tagText}
      </Tag>
      {editing ? (
        <IconButton
          bordered
          icon={X}
          label={removeLabel}
          disabled={removeDisabled}
          onClick={() => onRemove(line)}
          className="disabled:cursor-not-allowed disabled:opacity-40"
        />
      ) : (
        <IconButton
          bordered
          icon={Phone}
          label={callLabel}
          onClick={() => onCall(line)}
        />
      )}
    </div>
  );
};

export default CallRow;
